import React, { useState, useEffect, useContext } from "react";
import Sidebar from "../../components/layout/Sidebar";
import Navbar from "../../components/layout/Navbar";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { saveProcess, getProcesses } from "../../services/reference.service";
import { 
  ChevronLeft, 
  Save, 
  Layers, 
  Fingerprint, 
  Type, 
  Loader2, 
  CheckCircle2, 
  AlertCircle 
} from "lucide-react";

export default function CreateProcess() {
  const navigate = useNavigate();
  const { processId } = useParams();
  const { user } = useContext(AuthContext);

  const [formData, setFormData] = useState({
    ProcessId: null,
    ProcessCode: "",
    ProcessName: "",
    State: 1,
    UserCreated: user?.dni || "system",
  });

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [status, setStatus] = useState({ type: "", msg: "" });

  // Cargar datos si es edición
  useEffect(() => {
    if (processId) {
      const loadProcessData = async () => {
        setFetching(true);
        const token = localStorage.getItem("token") || "";
        const data = await getProcesses(token);
        const processToEdit = data.find(p => p.id === parseInt(processId));
        
        if (processToEdit) {
          setFormData({
            ProcessId: processToEdit.id,
            ProcessCode: processToEdit.processCode,
            ProcessName: processToEdit.processName,
            State: processToEdit.state || 1,
            UserCreated: processToEdit.userCreated,
          });
        }
        setFetching(false);
      };
      loadProcessData();
    }
  }, [processId]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: "", msg: "" });

    const token = localStorage.getItem("token") || "";
    const result = await saveProcess(token, formData);

    setLoading(false);

    if (result.success === 1) {
      setStatus({ type: "success", msg: "Proceso guardado correctamente." });
      setTimeout(() => navigate("/processes"), 1500);
    } else {
      setStatus({ type: "error", msg: result.message || "Error al guardar el proceso." });
    }
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden text-slate-900">
      <Sidebar />

      <main className="flex-1 flex flex-col overflow-hidden">
        <Navbar />

        {/* HEADER */}
        <div className="px-8 py-5 bg-white border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate("/processes")}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-900"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <div>
              <span className="text-[10px] font-black text-blue-600 uppercase tracking-[0.2em]">Configuración</span>
              <h2 className="text-2xl font-black text-slate-900 leading-none">
                {processId ? "Editar Proceso Maestro" : "Nuevo Proceso Maestro"}
              </h2>
            </div>
          </div>
        </div>

        {/* CONTENIDO */}
        <div className="flex-1 overflow-y-auto p-6 md:p-10">
          <div className="max-w-2xl mx-auto">
            
            {fetching ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="w-10 h-10 animate-spin text-blue-500 mb-4" />
                <p className="text-slate-400 font-bold tracking-widest">CARGANDO DATOS...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                
                <div className="bg-white rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-200 overflow-hidden">
                  <div className="p-8 md:p-10 space-y-8">
                    
                    <div className="flex items-center gap-4 mb-2">
                      <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600">
                        <Layers className="w-6 h-6" />
                      </div>
                      <p className="text-sm text-slate-500 leading-relaxed italic">
                        Define el código único y el nombre descriptivo para este estándar operativo.
                      </p>
                    </div>

                    {/* Código del Proceso */}
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 text-xs font-black text-slate-400 uppercase tracking-widest ml-1">
                        <Fingerprint className="w-3 h-3" /> Identificador / Código
                      </label>
                      <input
                        type="text"
                        name="ProcessCode"
                        value={formData.ProcessCode}
                        onChange={handleChange}
                        className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-5 py-4 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all font-bold text-slate-700 placeholder:text-slate-300"
                        placeholder="Ej. P001-MAN"
                        required
                      />
                    </div>

                    {/* Nombre del Proceso */}
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 text-xs font-black text-slate-400 uppercase tracking-widest ml-1">
                        <Type className="w-3 h-3" /> Nombre del Proceso
                      </label>
                      <input
                        type="text"
                        name="ProcessName"
                        value={formData.ProcessName}
                        onChange={handleChange}
                        className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-5 py-4 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all font-bold text-slate-700 placeholder:text-slate-300"
                        placeholder="Ej. Inspección de Turbinas"
                        required
                      />
                    </div>

                    {/* Feedback de Estado */}
                    {status.msg && (
                      <div className={`flex items-center gap-3 p-4 rounded-2xl border ${
                        status.type === "success" 
                          ? "bg-emerald-50 border-emerald-100 text-emerald-600" 
                          : "bg-red-50 border-red-100 text-red-600"
                      } animate-in fade-in zoom-in duration-300`}>
                        {status.type === "success" ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                        <span className="text-sm font-bold">{status.msg}</span>
                      </div>
                    )}
                  </div>

                  {/* Footer Interno del Card */}
                  <div className="bg-slate-50 p-6 border-t border-slate-100 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => navigate("/processes")}
                      className="px-6 py-3 rounded-xl font-bold text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 transition-all"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className={`flex items-center gap-2 px-10 py-3 rounded-xl font-black text-white transition-all shadow-lg active:scale-95 ${
                        loading ? "bg-slate-400 cursor-not-allowed" : "bg-slate-900 hover:bg-blue-600 shadow-slate-900/10"
                      }`}
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                      {loading ? "Guardando..." : "Guardar Estándar"}
                    </button>
                  </div>
                </div>

              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}